/* eslint-disable react/no-unused-prop-types */
import React, { useState, useCallback } from 'react';
import TabView from '@components/TabView';
import { Table, TableSize } from '@ui/molecules/Table';
import { Button } from '@ui';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { translate } from '@utils/i18n';
import AddTermModal from './AddTermModal';
const namespace = 'features.settings.ai_inspection_manager.grammar_and_style';
export default function TermsManagement({ advancedSettings, onUpdateSettings }) {
    const [isExcludeModalOpen, setIsExcludeModalOpen] = useState(false);
    const [isIncludeModalOpen, setIsIncludeModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [termToDelete, setTermToDelete] = useState(null);
    const [selectedExcludedTerms, setSelectedExcludedTerms] = useState([]);
    const [selectedIncludedTerms, setSelectedIncludedTerms] = useState([]);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    const [bulkDeleteType, setBulkDeleteType] = useState(null);
    const handleAddExcludedTerm = useCallback((term) => {
        if (!advancedSettings.excludedTerms.includes(term)) {
            const updatedSettings = Object.assign(Object.assign({}, advancedSettings), { excludedTerms: [...advancedSettings.excludedTerms, term] });
            onUpdateSettings(updatedSettings);
        }
    }, [advancedSettings, onUpdateSettings]);
    const handleAddIncludedTerm = useCallback((term) => {
        if (!advancedSettings.includedTerms.includes(term)) {
            const updatedSettings = Object.assign(Object.assign({}, advancedSettings), { includedTerms: [...advancedSettings.includedTerms, term] });
            onUpdateSettings(updatedSettings);
        }
    }, [advancedSettings, onUpdateSettings]);
    const handleDeleteExcludedTerm = useCallback((term) => {
        setTermToDelete({ term, type: 'excluded' });
        setDeleteConfirmOpen(true);
    }, []);
    const handleDeleteIncludedTerm = useCallback((term) => {
        setTermToDelete({ term, type: 'included' });
        setDeleteConfirmOpen(true);
    }, []);
    const confirmDeleteTerm = useCallback(() => {
        if (!termToDelete)
            return;
        if (termToDelete.type === 'excluded') {
            const updatedSettings = Object.assign(Object.assign({}, advancedSettings), { excludedTerms: advancedSettings.excludedTerms.filter((term) => term !== termToDelete.term) });
            onUpdateSettings(updatedSettings);
        }
        else {
            const updatedSettings = Object.assign(Object.assign({}, advancedSettings), { includedTerms: advancedSettings.includedTerms.filter((term) => term !== termToDelete.term) });
            onUpdateSettings(updatedSettings);
        }
        setDeleteConfirmOpen(false);
        setTermToDelete(null);
    }, [termToDelete, advancedSettings, onUpdateSettings]);
    const cancelDeleteTerm = useCallback(() => {
        setDeleteConfirmOpen(false);
        setTermToDelete(null);
    }, []);
    const handleExcludedSelectionChange = useCallback((term, checked) => {
        const key = String(term);
        setSelectedExcludedTerms((prev) => {
            if (checked) {
                return [...prev, key];
            }
            return prev.filter((t) => t !== key);
        });
    }, []);
    const handleExcludedSelectAll = useCallback((selectAll) => {
        if (selectAll) {
            setSelectedExcludedTerms(advancedSettings.excludedTerms);
        }
        else {
            setSelectedExcludedTerms([]);
        }
    }, [advancedSettings.excludedTerms]);
    const handleIncludedSelectionChange = useCallback((term, checked) => {
        const key = String(term);
        setSelectedIncludedTerms((prev) => {
            if (checked) {
                return [...prev, key];
            }
            return prev.filter((t) => t !== key);
        });
    }, []);
    const handleIncludedSelectAll = useCallback((selectAll) => {
        if (selectAll) {
            setSelectedIncludedTerms(advancedSettings.includedTerms);
        }
        else {
            setSelectedIncludedTerms([]);
        }
    }, [advancedSettings.includedTerms]);
    const handleBulkDeleteExcluded = useCallback(() => {
        if (selectedExcludedTerms.length > 0) {
            setBulkDeleteType('excluded');
            setBulkDeleteConfirmOpen(true);
        }
    }, [selectedExcludedTerms]);
    const handleBulkDeleteIncluded = useCallback(() => {
        if (selectedIncludedTerms.length > 0) {
            setBulkDeleteType('included');
            setBulkDeleteConfirmOpen(true);
        }
    }, [selectedIncludedTerms]);
    const confirmBulkDelete = useCallback(() => {
        if (!bulkDeleteType)
            return;
        if (bulkDeleteType === 'excluded') {
            const updatedSettings = Object.assign(Object.assign({}, advancedSettings), { excludedTerms: advancedSettings.excludedTerms.filter((term) => !selectedExcludedTerms.includes(term)) });
            onUpdateSettings(updatedSettings);
            setSelectedExcludedTerms([]);
        }
        else {
            const updatedSettings = Object.assign(Object.assign({}, advancedSettings), { includedTerms: advancedSettings.includedTerms.filter((term) => !selectedIncludedTerms.includes(term)) });
            onUpdateSettings(updatedSettings);
            setSelectedIncludedTerms([]);
        }
        setBulkDeleteConfirmOpen(false);
        setBulkDeleteType(null);
    }, [bulkDeleteType, selectedExcludedTerms, selectedIncludedTerms, advancedSettings, onUpdateSettings]);
    const cancelBulkDelete = useCallback(() => {
        setBulkDeleteConfirmOpen(false);
        setBulkDeleteType(null);
    }, []);
    const excludedTermsData = advancedSettings.excludedTerms.map((term) => ({ term }));
    const includedTermsData = advancedSettings.includedTerms.map((term) => ({ term }));
    const excludedTermsColumns = [
        {
            label: translate('term_or_abbreviation', { namespace }),
            dataKey: 'term',
            columnType: 'primary',
            width: '100%'
        },
        {
            label: translate('actions', { namespace }),
            dataKey: 'actions',
            align: 'right',
            width: '1px',
            renderer: ({ item }) => (React.createElement(Button, { onClick: () => handleDeleteExcludedTerm(item.term), title: translate('delete_term', { namespace }) },
                React.createElement("i", { className: "fa-light fa-trash-can" })))
        }
    ];
    const includedTermsColumns = [
        {
            label: translate('term_or_abbreviation', { namespace }),
            dataKey: 'term',
            columnType: 'primary',
            width: '100%'
        },
        {
            label: translate('actions', { namespace }),
            dataKey: 'actions',
            align: 'right',
            width: '1px',
            renderer: ({ item }) => (React.createElement(Button, { onClick: () => handleDeleteIncludedTerm(item.term), title: translate('delete_term', { namespace }) },
                React.createElement("i", { className: "fa-light fa-trash-can" })))
        }
    ];
    const tabs = [
        {
            id: 'excluded-terms',
            label: translate('excluded_terms_tab', { namespace }),
            content: (React.createElement("div", { className: "grammar-and-style-tab__terms-section" },
                React.createElement("p", { className: "grammar-and-style-tab__description" }, translate('excluded_terms_description', { namespace })),
                React.createElement("div", { className: `grammar-and-style-tab__actions ${selectedExcludedTerms.length > 0 ? 'grammar-and-style-tab__actions--with-selection' : ''}` },
                    selectedExcludedTerms.length > 0 && (React.createElement(Button, { onClick: handleBulkDeleteExcluded },
                        React.createElement("i", { className: "fa-light fa-trash-can" }),
                        " ",
                        translate('delete_selected', { namespace }),
                        " (",
                        selectedExcludedTerms.length,
                        ")")),
                    React.createElement(Button, { onClick: () => setIsExcludeModalOpen(true) },
                        React.createElement("i", { className: "fa-light fa-plus" }),
                        translate('add_term_or_abbreviation', { namespace }))),
                excludedTermsData.length > 0 ? (React.createElement(Table, { columns: excludedTermsColumns, data: excludedTermsData, size: TableSize.Full, variant: "detail", selectable: true, selectedItems: selectedExcludedTerms, onSelectionChange: handleExcludedSelectionChange, onSelectAll: handleExcludedSelectAll, getItemId: (item) => item.term })) : (React.createElement("div", { className: "grammar-and-style-tab__empty-state" }, translate('no_excluded_terms', { namespace })))))
        },
        {
            id: 'included-terms',
            label: translate('included_terms_tab', { namespace }),
            content: (React.createElement("div", { className: "grammar-and-style-tab__terms-section" },
                React.createElement("p", { className: "grammar-and-style-tab__description" }, translate('included_terms_description', { namespace })),
                React.createElement("div", { className: `grammar-and-style-tab__actions ${selectedIncludedTerms.length > 0 ? 'grammar-and-style-tab__actions--with-selection' : ''}` },
                    selectedIncludedTerms.length > 0 && (React.createElement(Button, { onClick: handleBulkDeleteIncluded },
                        React.createElement("i", { className: "fa-light fa-trash-can" }),
                        " ",
                        translate('delete_selected', { namespace }),
                        " (",
                        selectedIncludedTerms.length,
                        ")")),
                    React.createElement(Button, { onClick: () => setIsIncludeModalOpen(true) },
                        React.createElement("i", { className: "fa-light fa-plus" }),
                        translate('add_term_or_abbreviation', { namespace }))),
                includedTermsData.length > 0 ? (React.createElement(Table, { columns: includedTermsColumns, data: includedTermsData, size: TableSize.Full, variant: "detail", selectable: true, selectedItems: selectedIncludedTerms, onSelectionChange: handleIncludedSelectionChange, onSelectAll: handleIncludedSelectAll, getItemId: (item) => item.term })) : (React.createElement("div", { className: "grammar-and-style-tab__empty-state" }, translate('no_included_terms', { namespace })))))
        }
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(TabView, { tabs: tabs }),
        React.createElement(AddTermModal, { isOpen: isExcludeModalOpen, onClose: () => setIsExcludeModalOpen(false), onAdd: handleAddExcludedTerm, modalType: "exclude" }),
        React.createElement(AddTermModal, { isOpen: isIncludeModalOpen, onClose: () => setIsIncludeModalOpen(false), onAdd: handleAddIncludedTerm, modalType: "include" }),
        deleteConfirmOpen && termToDelete && (React.createElement(ConfirmDialog, { title: translate('delete_term_title', { namespace }), description: translate('delete_term_description', { namespace, term: termToDelete.term }), onConfirm: confirmDeleteTerm, onCancel: cancelDeleteTerm, yesLabel: translate('delete', { namespace }), noLabel: translate('cancel', { namespace: 'features.commons' }) })),
        bulkDeleteConfirmOpen && bulkDeleteType && (React.createElement(ConfirmDialog, { title: translate('bulk_delete_terms_title', { namespace }), description: translate('bulk_delete_terms_description', {
                namespace,
                count: bulkDeleteType === 'excluded' ? selectedExcludedTerms.length : selectedIncludedTerms.length
            }), onConfirm: confirmBulkDelete, onCancel: cancelBulkDelete, yesLabel: translate('delete', { namespace }), noLabel: translate('cancel', { namespace: 'features.commons' }) }))));
}
